<script setup lang="ts">
import { computed, type HTMLAttributes } from "vue"
import { ThemeButton, ThemeImg } from "~/components/theme"
import { useConcept } from "~/composables/concept"
import { cn } from "~/lib/utils"

const { concept } = useConcept()

const merch = computed(() => concept.value?.merch)
const firstParagraph = computed(() => merch?.value?.paragraphs[0])
const remainingParagraphs = computed(() => merch?.value?.paragraphs.slice(1) ?? [])

const { 
    class: cl,
    id
} = defineProps<{
    class?: HTMLAttributes["class"]
    id?: string
}>()
</script>

<template>
    <section
        v-if="merch"
        :id="id"
        :class="cn('space-y-10 md:space-y-12', cl)"
    >
        <div class="flex flex-col gap-8 lg:grid lg:grid-cols-2 lg:gap-x-12 p-6 sm:p-8 lg:p-12 rounded-lg bg-featured-section-background text-featured-section-text overflow-hidden">
            <!-- Mobile: contents + order stacks intro / image / body. Desktop: one text column. -->
            <div class="contents lg:flex lg:flex-col lg:gap-4 lg:col-start-1 min-w-0">
                <div class="order-1 space-y-4 min-w-0">
                    <h2 class="text-2xl sm:text-3xl lg:text-4xl font-headline text-featured-section-text break-words">
                        {{ merch.title }}
                    </h2>
                    <p
                        v-if="firstParagraph"
                        class="text-base leading-relaxed text-featured-section-text break-words"
                        v-html="firstParagraph"
                    />
                </div>

                <div class="order-3 space-y-6 min-w-0">
                    <div
                        v-if="remainingParagraphs.length"
                        class="space-y-4"
                    >
                        <p
                            v-for="(paragraph, index) in remainingParagraphs"
                            :key="index"
                            class="text-base leading-relaxed text-featured-section-text break-words"
                            v-html="paragraph"
                        />
                    </div>
                    <div class="flex flex-col gap-4 sm:flex-row sm:flex-wrap text-center justify-start">
                        <a
                            :href="merch.url"
                            target="_blank"
                            rel="noopener noreferrer"
                            tabindex="0"
                            :aria-label="merch.buttonText"
                            class="w-full sm:w-auto flex justify-center"
                        >
                            <ThemeButton
                                class="w-full sm:w-auto max-w-full bg-featured-section-button-background hover:bg-featured-section-button-hover-background text-featured-section-button-text hover:text-featured-section-button-hover-text whitespace-normal text-center"
                            >
                                <span class="break-words">{{ merch.buttonText }}</span>
                            </ThemeButton>
                        </a>
                    </div>
                </div>
            </div>

            <div class="order-2 relative min-h-64 aspect-[4/3] lg:aspect-auto lg:min-h-0 lg:h-full lg:col-start-2">
                <ThemeImg
                    src="/global/merch/merch.jpg"
                    :alt="merch.imageAlt || merch.title || ''"
                    class="max-lg:size-full lg:absolute lg:inset-0 w-full h-full object-cover rounded-lg shadow-xl"
                />
            </div>
        </div>
    </section>
</template>
